import React, { useCallback, useEffect, useId, useState } from "react";
import {
  CheckboxProps,
  CheckedState,
  Label,
  styled,
  Text,
  Checkbox as TGCheckbox,
  XStack,
} from "tamagui";

import { Check, Dash } from "../../icons";

const CheckBox = styled(TGCheckbox, {
  name: "CheckBox",
  cursor: "pointer",
  outlineWidth: 0,
  outlineColor: "transparent",
  variants: {
    variant: {
      default: {
        borderColor: "$gray-800",
        backgroundColor: "white",
        hoverStyle: {
          backgroundColor: "$gray-200",
        },
        focusStyle: {
          borderColor: "$lightblue-700",
          backgroundColor: "white",
        },
        disabledStyle: {
          borderColor: "$gray-200",
          backgroundColor: "white",
          cursor: "not-allowed",
        },
      },
    },
    sizes: {
      md: {
        borderWidth: "$0.25",
        borderRadius: "$1",
        height: "$2",
        width: "$2",
      },
    },
    activated: {
      true: {
        backgroundColor: "$lightgreen-500",
        borderColor: "$lightgreen-500",

        hoverStyle: {
          backgroundColor: "$lightgreen-500",
          borderColor: "$lightgreen-500",
        },
        focusStyle: {
          backgroundColor: "$lightgreen-500",
          borderColor: "$lightgreen-500",
        },
      },
      false: {},
      indeterminate: {
        backgroundColor: "$lightgreen-500",
        borderColor: "$lightgreen-500",

        hoverStyle: {
          backgroundColor: "$lightgreen-500",
          borderColor: "$lightgreen-500",
        },
        focusStyle: {
          backgroundColor: "$lightgreen-500",
          borderColor: "$lightgreen-500",
        },
      },
    },
  } as const,
  defaultVariants: {
    sizes: "md",
    variant: "default",
  },
});

export const CheckBoxLabelText = styled(Text, {
  name: "CheckBoxLabelText",
  fontFamily: "$body",
  color: "$gray-950",
  variants: {
    font: {
      md: {
        fontWeight: "$5",
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
  defaultVariants: {
    font: "md",
  },
});

export const Checkbox: React.FC<CheckboxProps & { sizes?: "md" }> = ({
  onCheckedChange,
  checked = false,
  sizes = "md",
  ...rest
}) => {
  const [checkedState, setChecked] = useState<CheckedState>(checked);

  useEffect(() => {
    setChecked(checked);
  }, [checked]);

  return (
    <CheckBox
      sizes={sizes}
      checked={checkedState}
      activated={checkedState}
      onCheckedChange={(state) => {
        setChecked(state);
        onCheckedChange?.(state);
      }}
      {...rest}
    >
      <TGCheckbox.Indicator>
        {checkedState === "indeterminate" ? <Dash /> : <Check />}
      </TGCheckbox.Indicator>
    </CheckBox>
  );
};

export const CheckBoxWithLabel: React.FC<
  CheckboxProps & { label?: string | null; renderLabel?: React.ReactNode }
> = ({ label, renderLabel, ...rest }) => {
  const id = useId();

  const RenderLabel = useCallback(() => {
    if (renderLabel) return renderLabel;
    return <CheckBoxLabelText>{label}</CheckBoxLabelText>;
  }, [label, renderLabel]);

  return (
    <XStack gap={"$1"} alignItems="center">
      <Checkbox id={id} {...rest} />
      <Label htmlFor={id}>
        <RenderLabel />
      </Label>
    </XStack>
  );
};
